import React, { useEffect, useState } from "react";
import { Table } from "antd";
import type { TableColumnsType } from "antd";
import { GetDashboardTicketCount } from "../../../../utils/api.constant";
import { eResultCode } from "../../../../utils/enum";
import useFetch from "../../../../hooks/useFetch";
import ExportToCSV from "../../../../components/exporttocsv/ExportToCsv";
import DateUtils from "../../../../utils/date";
import { TDropdownOptions } from "../../../../types/config";
import { dateOnlyFormat, monthOnlyFormat } from "../../../../utils/constants";

type DataType2 = {
  key: string;
  parameter: string;
  dataIndex?: string;
  todayService: number;
  todayInstall: number;
  todayOthers: number;
  last7Service: number;
  last7Install: number;
  last7Others: number;
  monthService: number;
  monthInstall: number;
  monthOthers: number;
};

type ParameterTableProps = {
  viewDate: string;
  customerIds: string[];
  servicePartnerIds: string[];
  customerOptions: TDropdownOptions[];
  partnerOptions: TDropdownOptions[];
  onDataFetch: (data: DataType2[]) => void;
};

const ParameterTable: React.FC<ParameterTableProps> = ({
  viewDate,
  customerIds,
  servicePartnerIds,
  customerOptions,
  partnerOptions,
  onDataFetch,
}) => {
  const { post } = useFetch();
  const [isLoading, setIsLoading] = useState(false);
  const [tableData, setTableData] = useState<DataType2[]>([]);

  const columns: TableColumnsType<DataType2> = [
    {
      title: (
        <>
          {/* <ExportToCSV
            data={exportData}
            headers={exportHeaders}
            fileName={`parameters_${DateUtils.format(new Date())}.csv`}
          /> */}
          <span style={{ paddingLeft: "10px" }}>Closed Ticket Info</span>
        </>
      ),
      dataIndex: "parameter",
      key: "parameter",
      fixed: "left",
      width: 250,
      className: "font-medium cell-bordered row-bordered",
    },
    {
      title: (
        <>
          <span
            style={{
              lineHeight: "10px !important",
              fontSize: "12px",
              padding: "5px",
            }}
          >
            {DateUtils.format(viewDate, dateOnlyFormat)}
          </span>
        </>
      ),
      children: [
        {
          title: () => (
            <span style={{ color: "#07776C", fontSize: 12 }}>Service</span>
          ),
          dataIndex: "todayService",
          key: "todayService",
          align: "center",
        },
        {
          title: () => (
            <span style={{ color: "#07776C", fontSize: 12 }}>Install</span>
          ),
          dataIndex: "todayInstall",
          key: "todayInstall",
          align: "center",
        },
        {
          title: () => (
            <span style={{ color: "#07776C", fontSize: 12 }}>Others</span>
          ),
          dataIndex: "todayOthers",
          key: "todayOthers",
          align: "center",
          className: "cell-bordered",
        },
      ],
    },
    {
      title: "Last 7 days",
      className: "row-bordered",
      children: [
        {
          title: () => (
            <span style={{ color: "#07776C", fontSize: 12 }}>Service</span>
          ),
          dataIndex: "last7Service",
          key: "last7Service",
          align: "center",
        },
        {
          title: () => (
            <span style={{ color: "#07776C", fontSize: 12 }}>Install</span>
          ),
          dataIndex: "last7Install",
          key: "last7Install",
          align: "center",
        },
        {
          title: () => (
            <span style={{ color: "#07776C", fontSize: 12 }}>Others</span>
          ),
          dataIndex: "last7Others",
          key: "last7Others",
          align: "center",
          className: "cell-bordered",
        },
      ],
    },
    {
      title: DateUtils.format(viewDate, monthOnlyFormat),
      className: "row-bordered",
      children: [
        {
          title: () => (
            <span style={{ color: "#07776C", fontSize: 12 }}>Service</span>
          ),
          dataIndex: "monthService",
          key: "monthService",
          align: "center",
        },
        {
          title: () => (
            <span style={{ color: "#07776C", fontSize: 12 }}>Install</span>
          ),
          dataIndex: "monthInstall",
          key: "monthInstall",
          align: "center",
        },
        {
          title: () => (
            <span style={{ color: "#07776C", fontSize: 12 }}>Others</span>
          ),
          dataIndex: "monthOthers",
          key: "monthOthers",
          align: "center",
          // className:"cell-bordered"
        },
      ],
    },
  ];

  const GetTicketCount = async () => {
    setIsLoading(true);
    try {
      const payload = {
        data: {
          viewDate: DateUtils.getDateStringWithOutTimezone(
            new Date(viewDate).toDateString()
          ),
          IsAllCustomerRequest: customerIds?.includes("ALL") ? 1 : 0,
          IsAllServicePartnerRequest: servicePartnerIds?.includes("ALL")
            ? 1
            : 0,
          customerId: customerIds?.includes("ALL")
            ? customerOptions
                ?.filter((option) => option.value !== "ALL")
                .map((option) => option.value)
            : customerIds,
          servicePartnerId: servicePartnerIds?.includes("ALL")
            ? partnerOptions
                ?.filter((option) => option.value !== "ALL")
                .map((option) => option.value)
            : servicePartnerIds,
        },
      };

      const response: any = await post(GetDashboardTicketCount, payload);
      if (response) {
        const { data, dataResponse } = response;
        const { returnCode } = dataResponse;

        if (returnCode === eResultCode.SUCCESS) {
          // Extracting counts from the response data
          const formattedData: DataType2[] = [
            {
              key: "1",
              parameter: "Call logged",
              todayService: data.callLogged.today.serviceCount,
              todayInstall: data.callLogged.today.installCount, // Simulating install count
              todayOthers: data.callLogged.today.othersCount, // Simulating others count
              last7Service: data.callLogged.lastSevenDay.serviceCount,
              last7Install: data.callLogged.lastSevenDay.installCount, // Simulating install count
              last7Others: data.callLogged.lastSevenDay.othersCount, // Simulating others count
              monthService: data.callLogged.inThisMonth.serviceCount,
              monthInstall: data.callLogged.inThisMonth.installCount, // Simulating install count
              monthOthers: data.callLogged.inThisMonth.othersCount, // Simulating others count
            },
            {
              key: "2",
              parameter: "Closed within TAT",
              todayService: data.closedWithinTAT.today.serviceCount,
              todayInstall: data.closedWithinTAT.today.installCount, // Simulating install count
              todayOthers: "-", // Simulating others count
              last7Service: data.closedWithinTAT.lastSevenDay.serviceCount,
              last7Install: data.closedWithinTAT.lastSevenDay.installCount, // Simulating install count
              last7Others: "-", // Simulating others count
              monthService: data.closedWithinTAT.inThisMonth.serviceCount,
              monthInstall: data.closedWithinTAT.inThisMonth.installCount, // Simulating install count
              monthOthers: "-", // Simulating others count
            },
            {
              key: "3",
              parameter: "Closed out of TAT",
              todayService: data.closedOutOfTAT.today.serviceCount,
              todayInstall: data.closedOutOfTAT.today.installCount, // Simulating install count
              todayOthers: "-", // Simulating others count
              last7Service: data.closedOutOfTAT.lastSevenDay.serviceCount,
              last7Install: data.closedOutOfTAT.lastSevenDay.installCount, // Simulating install count
              last7Others: "-", // Simulating others count
              monthService: data.closedOutOfTAT.inThisMonth.serviceCount,
              monthInstall: data.closedOutOfTAT.inThisMonth.installCount, // Simulating install count
              monthOthers: "-", // Simulating others count
            },
            // {
            //   key: "4",
            //   parameter: "Call open - Total",
            //   todayService: data.callOpenTotal.today.serviceCount,
            //   todayInstall: data.callOpenTotal.today.installCount, // Simulating install count
            //   todayOthers: data.callOpenTotal.today.othersCount, // Simulating others count
            //   last7Service: data.callOpenTotal.lastSevenDay.serviceCount,
            //   last7Install: data.callOpenTotal.lastSevenDay.installCount, // Simulating install count
            //   last7Others: data.callOpenTotal.lastSevenDay.othersCount, // Simulating others count
            //   monthService: data.callOpenTotal.inThisMonth.serviceCount,
            //   monthInstall: data.callOpenTotal.inThisMonth.installCount, // Simulating install count
            //   monthOthers: data.callOpenTotal.inThisMonth.othersCount, // Simulating others count
            // },
            // {
            //   key: "5",
            //   parameter: "Call open - Attended",
            //   todayService: data.callOpenAttended.today.serviceCount,
            //   todayInstall: data.callOpenAttended.today.installCount, // Simulating install count
            //   todayOthers: data.callOpenAttended.today.othersCount, // Simulating others count
            //   last7Service: data.callOpenAttended.lastSevenDay.serviceCount,
            //   last7Install: data.callOpenAttended.lastSevenDay.installCount, // Simulating install count
            //   last7Others: data.callOpenAttended.lastSevenDay.othersCount, // Simulating others count
            //   monthService: data.callOpenAttended.inThisMonth.serviceCount,
            //   monthInstall: data.callOpenAttended.inThisMonth.installCount, // Simulating install count
            //   monthOthers: data.callOpenAttended.inThisMonth.othersCount, // Simulating others count
            // },
            // {
            //   key: "6",
            //   parameter: "Call open - Dealer dependent",
            //   todayService: data.callOpenDealerDependency.today.serviceCount,
            //   todayInstall: data.callOpenDealerDependency.today.installCount, // Simulating install count
            //   todayOthers: data.callOpenDealerDependency.today.othersCount, // Simulating others count
            //   last7Service:
            //     data.callOpenDealerDependency.lastSevenDay.serviceCount,
            //   last7Install:
            //     data.callOpenDealerDependency.lastSevenDay.installCount, // Simulating install count
            //   last7Others:
            //     data.callOpenDealerDependency.lastSevenDay.othersCount, // Simulating others count
            //   monthService:
            //     data.callOpenDealerDependency.inThisMonth.serviceCount,
            //   monthInstall:
            //     data.callOpenDealerDependency.inThisMonth.installCount, // Simulating install count
            //   monthOthers:
            //     data.callOpenDealerDependency.inThisMonth.othersCount, // Simulating others count
            // },
            // {
            //   key: "7",
            //   parameter: "Call open - Hold spares",
            //   todayService: data.callOpenHoldSpares.today.serviceCount,
            //   todayInstall: data.callOpenHoldSpares.today.installCount, // Simulating install count
            //   todayOthers: data.callOpenHoldSpares.today.othersCount, // Simulating others count
            //   last7Service: data.callOpenHoldSpares.lastSevenDay.serviceCount,
            //   last7Install: data.callOpenHoldSpares.lastSevenDay.installCount, // Simulating install count
            //   last7Others: data.callOpenHoldSpares.lastSevenDay.othersCount, // Simulating others count
            //   monthService: data.callOpenHoldSpares.inThisMonth.serviceCount,
            //   monthInstall: data.callOpenHoldSpares.inThisMonth.installCount, // Simulating install count
            //   monthOthers: data.callOpenHoldSpares.inThisMonth.othersCount, // Simulating others count
            // },
            // {
            //   key: "8",
            //   parameter: "Call open - Within TAT",
            //   todayService: data.callOpenWithinTAT.today.serviceCount,
            //   todayInstall: data.callOpenWithinTAT.today.installCount, // Simulating install count
            //   todayOthers: data.callOpenWithinTAT.today.othersCount, // Simulating others count
            //   last7Service: data.callOpenWithinTAT.lastSevenDay.serviceCount,
            //   last7Install: data.callOpenWithinTAT.lastSevenDay.installCount, // Simulating install count
            //   last7Others: data.callOpenWithinTAT.lastSevenDay.othersCount, // Simulating others count
            //   monthService: data.callOpenWithinTAT.inThisMonth.serviceCount,
            //   monthInstall: data.callOpenWithinTAT.inThisMonth.installCount, // Simulating install count
            //   monthOthers: data.callOpenWithinTAT.inThisMonth.othersCount, // Simulating others count
            // },
            // {
            //   key: "9",
            //   parameter: "Call open - Out of TAT",
            //   todayService: data.callOpenOutOfTAT.today.serviceCount,
            //   todayInstall: data.callOpenOutOfTAT.today.installCount, // Simulating install count
            //   todayOthers: data.callOpenOutOfTAT.today.othersCount, // Simulating others count
            //   last7Service: data.callOpenOutOfTAT.lastSevenDay.serviceCount,
            //   last7Install: data.callOpenOutOfTAT.lastSevenDay.installCount, // Simulating install count
            //   last7Others: data.callOpenOutOfTAT.lastSevenDay.othersCount, // Simulating others count
            //   monthService: data.callOpenOutOfTAT.inThisMonth.serviceCount,
            //   monthInstall: data.callOpenOutOfTAT.inThisMonth.installCount, // Simulating install count
            //   monthOthers: data.callOpenOutOfTAT.inThisMonth.othersCount, // Simulating others count
            // },
            // {
            //   key: "10",
            //   parameter: "Call open - Pending/unattended",
            //   todayService: data.callOpenPendingUnattended.today.serviceCount,
            //   todayInstall: data.callOpenPendingUnattended.today.installCount, // Simulating install count
            //   todayOthers: data.callOpenPendingUnattended.today.othersCount, // Simulating others count
            //   last7Service:
            //     data.callOpenPendingUnattended.lastSevenDay.serviceCount,
            //   last7Install:
            //     data.callOpenPendingUnattended.lastSevenDay.installCount, // Simulating install count
            //   last7Others:
            //     data.callOpenPendingUnattended.lastSevenDay.othersCount, // Simulating others count
            //   monthService:
            //     data.callOpenPendingUnattended.inThisMonth.serviceCount,
            //   monthInstall:
            //     data.callOpenPendingUnattended.inThisMonth.installCount, // Simulating install count
            //   monthOthers:
            //     data.callOpenPendingUnattended.inThisMonth.othersCount, // Simulating others count
            // },
          ];
          setTableData(formattedData);
          setIsLoading(false);
        }
      }
    } catch (error) {
      console.error("Error fetching ticket count:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (customerIds?.length && servicePartnerIds?.length) {
      GetTicketCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    viewDate,
    JSON.stringify(customerIds),
    JSON.stringify(servicePartnerIds),
  ]);

  useEffect(() => {
    onDataFetch(tableData);
  }, [tableData]);

  return (
    <Table
      // bordered
      loading={isLoading}
      columns={columns}
      dataSource={tableData}
      size="small"
      pagination={false}
      scroll={{ x: "max-content" }}
      // rowClassName={(record, index) => (index % 2 === 0 ? "green-row" : "")} // Add class to alternate rows
    />
  );
};

export default ParameterTable;
